import dxDrawer from "devextreme/ui/drawer";
import dxLoadPanel from "devextreme/ui/load_panel";
import dxToolbar from "devextreme/ui/toolbar";
import HermesNotifier from "../notificacoes/HermesNotifier";
import { sairDoEponto } from "../services/auth/utils";
import { handleError } from "../telas/jornada/Utils";
import ControleSessao from "./ControleSessao";
import ControleTabPadrao from "./ControleTabPadrao";
import {
    ALTURA_TOOLBAR_MOBILE,
    ALTURA_TOPBAR,
    LARGURA_MENU_COLAPSADO,
    LARGURA_MENU_EXPANDIDO,
} from "./design-system/tokens";
import {
    publicarCatalogo,
    reiniciarCatalogo,
} from "./menu/catalogoTelas";
import { criarMenuLateral } from "./menu/MenuLateral";
import { montarItensMenu } from "./menu/montarMenu";
import { criarTopbar } from "./topbar/Topbar";

/**
 * Casca do layout global: menu lateral, menu superior e barra de abas.
 *
 * Existe porque MenuNavegacao e MenuNavegacaoEmbarcadora eram cópias quase
 * idênticas — o que já havia produzido divergências silenciosas, como o menu do
 * embarcador não colapsar ao abrir tela. Depois do redesign (JOR4-7365) as duas
 * ficaram praticamente iguais, e manter duas cópias só adiaria a próxima
 * divergência.
 *
 * As subclasses informam apenas o que muda entre um login e outro, pelos ganchos
 * no fim desta classe.
 */
export default class MenuNavegacaoBase {
    /**
     * @param {string} contextoLogado razão social exibida no menu superior
     * @param {{CAB_CODIGO: number, CAB_RAZAO: string}[]} filiaisLogadas
     * @param {string} rotuloTrocarContexto rótulo do item sentinela do mobile
     */
    constructor(contextoLogado, filiaisLogadas, rotuloTrocarContexto) {
        this.contextoLogado = contextoLogado;
        this.filiaisLogadas = filiaisLogadas;
        this.siglaUsuario = this.montarSiglaUsuario();

        /*
         * Filiais na forma de origem para o menu superior: `id` é o jfe_codigo e
         * `reason` o jfe_razao devolvidos por branches-allowed. O login renomeia
         * para CAB_CODIGO/CAB_RAZAO — nomenclatura do sistema de risco — e o
         * resto do front ainda depende disso, então a conversão fica aqui, e não
         * dentro do componente.
         */
        this.filiais = this.filiaisLogadas.map((filial) => ({
            id: filial.CAB_CODIGO,
            reason: filial.CAB_RAZAO,
        }));

        // Os dois itens sentinela abaixo existem só para o dxSelectBox do
        // toolbar mobile, que ainda não foi redesenhado: um widget de seleção
        // não tem onde pôr cabeçalho nem ação, então elas viravam opções.

        // Adiciona nome da empresa logada
        this.filiaisLogadas.unshift({
            disabled: true,
            CAB_CODIGO: -1,
            CAB_RAZAO: this.contextoLogado,
        });

        // Adiciona opção de trocar empresa
        this.filiaisLogadas.unshift({
            CAB_CODIGO: 0,
            CAB_RAZAO: rotuloTrocarContexto,
        });
    }

    montarSiglaUsuario() {
        const nomeCompletoUsuario = ControleSessao.getNomeUsuario().split(" ");
        const primeiraLetraNome = nomeCompletoUsuario[0].charAt(0);
        let primeiraLetraSobrenome = nomeCompletoUsuario[0].charAt(1);

        if (nomeCompletoUsuario[1]) {
            primeiraLetraSobrenome =
                nomeCompletoUsuario[0].charAt(0) === "C" &&
                nomeCompletoUsuario[1].charAt(0) === "U"
                    ? nomeCompletoUsuario[0].charAt(1)
                    : nomeCompletoUsuario[1].charAt(0);
        }

        return primeiraLetraNome + primeiraLetraSobrenome;
    }

    /**
     * Mesmo limiar que o resto da classe usa para escolher entre o menu superior
     * do redesign e o toolbar antigo. Avaliado uma vez, como antes: girar o
     * aparelho não reconstrói o layout.
     */
    ehMobile() {
        return window.innerWidth <= 450;
    }

    render() {
        // Layout novo, catálogo novo. A troca de empresa remonta tudo sem
        // recarregar a página, e sem isto o catálogo da empresa anterior
        // continuaria publicado até o `menus-allowed` da nova responder — tempo
        // em que a Início ofereceria telas de outra empresa. (JOR4-7663 / RN04)
        reiniciarCatalogo();

        this.renderDrawer = this.drawer();
        this.renderToolbar = this.toolbar(this.logout);

        this.renderControleTabPadrao = new ControleTabPadrao(
            this.renderDrawer.instance,
        );
        this.renderControleTabPadrao.render();

        // RN03: a aba em foco é a fonte da verdade do destaque no menu.
        this.renderControleTabPadrao.aoMudarTelaAtiva((classeAtiva) => {
            if (!this.menuLateralRender) return;
            this.menuLateralRender.atualizar({ classeAtiva });
        });

        // A tela de Início é a aba fixa do sistema: abre automaticamente ao
        // acessar o ePonto e não pode ser fechada. (JOR4-7363 / RN08)
        if (this.abreTelaInicio()) {
            this.renderControleTabPadrao.incluiTabFixa("Início", "Inicio");
        }

        this.autoHeight();
    }

    drawer() {
        /*
         * O modo colapsado não existe no mobile. Ele depende de hover para
         * abrir o submenu (RN04), que não existe em toque: um rail de 56px
         * deixaria todo grupo inalcançável. Fechado no mobile é fechado mesmo.
         *
         * E o painel sobrepõe o conteúdo em vez de espremê-lo: 260px de menu num
         * aparelho de 360px deixariam 100px de tela.
         */
        const mobile = this.ehMobile();
        this.temModoColapsado = !mobile;

        const content = "#drawer";
        const instance = new dxDrawer(content, {
            // Expandido ao carregar; colapsa sozinho ao abrir uma tela (RN02).
            opened: true,
            closeOnOutsideClick: true,
            elementAttr: { class: "drawer-menu-ds" },
            // minSize é o que transforma "fechado" em rail de ícones em vez de
            // sumir com o menu. revealMode "expand" anima a largura do painel;
            // "slide" deslizaria o painel inteiro para fora, mostrando o
            // pedaço errado do conteúdo colapsado.
            minSize: mobile ? 0 : LARGURA_MENU_COLAPSADO,
            maxSize: LARGURA_MENU_EXPANDIDO,
            revealMode: "expand",
            openedStateMode: mobile ? "overlap" : "shrink",
            onOptionChanged: ({ name, value }) => {
                if (name !== "opened" || !this.menuLateralRender) return;
                if (!this.temModoColapsado) return;
                this.menuLateralRender.atualizar({ colapsado: !value });
            },
            template: () => {
                /*
                 * Devolve sempre o MESMO elemento. O dxDrawer reinvoca o
                 * template a cada repaint, e devolver um <div> novo deixaria o
                 * painel vazio: o root do React continuaria preso ao nó
                 * descartado, e o autoHeight seguiria dimensionando esse nó. O
                 * menu sumiria sem erro no console.
                 */
                if (!this.elementoMenuLateral) {
                    this.elementoMenuLateral = $(
                        "<div class='menu-ds-raiz'>",
                    ).get(0);

                    this.menuLateralRender = criarMenuLateral(
                        this.elementoMenuLateral,
                    );
                    this.menuLateralRender.atualizar({
                        aoAbrirTela: (item) => this.abrirTela(item),
                    });

                    this.carregarItensMenu();
                }

                return $(this.elementoMenuLateral);
            },
        });

        return {
            content,
            instance,
        };
    }

    carregarItensMenu() {
        this.carregarMenusPermitidos()
            .then((allowedMenus) => {
                this.menuLateral = montarItensMenu(
                    allowedMenus,
                    this.incluiTelasInternas(),
                );
                this.menuLateralRender.atualizar({ itens: this.menuLateral });

                // A tela de Início monta antes desta resposta e precisa da
                // mesma lista para oferecer os itens de acesso rápido.
                // (JOR4-7663 / RN04)
                publicarCatalogo(this.menuLateral);
            })
            .catch((error) => {
                // Publica vazio para não deixar quem espera o catálogo preso
                // num "carregando" que nunca termina.
                publicarCatalogo([]);
                handleError(error);
            });
    }

    abrirTela(item) {
        try {
            if (!item) return;

            if (!item.classInvoke)
                return console.warn(
                    `Classe não informada para a tela ${item.text}`,
                );

            this.renderControleTabPadrao.incluiTab(item.text, item.classInvoke);

            // RN02: o modo colapsado é o estado padrão enquanto se navega.
            this.renderDrawer.instance.hide();
        } catch (e) {
            console.log(e);
        }
    }

    toolbar(logout) {
        if (window.innerWidth > 450) {
            $(".raster-app").prepend($("<div id='toolbar'></div>"));
            const content = $("#toolbar");
            this.toolbarDesktop(content, logout);
        } else {
            $(".raster-app").prepend($("<div id='toolbar-buttons'></div>"));
            const contentButtons = $("#toolbar-buttons");
            this.toolbarButtonsMobile(contentButtons, logout);
        }
    }

    toolbarDesktop(content, logout) {
        this.topbarRender = criarTopbar(content.get(0), {
            empresa: this.contextoLogado,
            filiais: this.filiais,
            nomeUsuario: ControleSessao.getNomeUsuario(),
            siglaUsuario: this.siglaUsuario,
            mostrarNovidades: process.env.ENVIRONMENT_WHITE_LABEL === "LOGAE",
            aoAlternarMenu: () => this.renderDrawer.instance.toggle(),
            aoAbrirNovidades: () => {
                window.open(process.env.LOGAE_RELEASES_URL, "_blank").focus();
            },
            aoTrocarEmpresa: () => this.trocarContexto(),
            aoSair: () => this.sairComAviso(logout),
            aoMontarNotificacoes: (elemento) => {
                // Chega null na desmontagem; e só monta uma vez, porque o
                // widget do Hermes abre socket e não é idempotente.
                if (!elemento || this.notificador) return;
                this.notificador = new HermesNotifier(elemento);
            },
        });

        return this.topbarRender;
    }

    /**
     * Menu superior do mobile, ainda no formato antigo. O redesign do protótipo
     * só especifica desktop.
     */
    toolbarButtonsMobile(content, logout) {
        return new dxToolbar(content, {
            width: "100%",
            items: [
                {
                    widget: "dxButton",
                    options: {
                        stylingMode: "text",
                        icon: "menu",
                        onClick: () => {
                            this.renderDrawer.instance.toggle();
                        },
                    },
                },
                {
                    widget: "dxSelectBox",
                    options: {
                        stylingMode: "text",
                        items: this.filiaisLogadas,
                        displayExpr: "CAB_RAZAO",
                        keyExpr: "CAB_CODIGO",
                        width: 180,
                        value: this.filiaisLogadas[1],
                        onValueChanged: (args) => {
                            if (args.value.CAB_CODIGO == 0) {
                                this.trocarContexto();
                            }
                        },
                    },
                },
                {
                    widget: "dxDropDownButton",
                    location: "after",
                    options: {
                        text: () => this.siglaUsuario,
                        elementAttr: { class: "menu-usuario" },
                        dropDownOptions: {
                            width: 230,
                        },
                        onItemClick: (e) => {
                            switch (e.itemData.name) {
                                case "Sair":
                                    this.sairComAviso(logout);
                                    break;
                            }
                        },
                        displayExpr: "name",
                        keyExpr: "id",
                        items: [
                            {
                                name: `Olá, ${ControleSessao.getNomeUsuario()}`,
                                disabled: true,
                                template: () => {
                                    return $(
                                        `<div style='padding-bottom: 10px; border-bottom: 1px dashed #020039;'>Olá, ${ControleSessao.getNomeUsuario()}</div>`,
                                    );
                                },
                            },
                            {
                                name: "Sair",
                                icon: "fas fa-sign-out-alt",
                            },
                        ],
                    },
                },
            ],
        });
    }

    /**
     * Volta ao estado inicial e reabre a seleção de empresa/filial. A lógica é a
     * mesma de antes (RN01), só saiu de dentro do onValueChanged do select.
     */
    trocarContexto() {
        // Remove HTML para voltar ao estado inicial
        $(".raster-app").empty();
        setTimeout(() => {
            // Fora do handler do React de propósito: unmount síncrono dentro de
            // um evento pode cair no meio de um render.
            this.destruirLayout();
            // Precisa de um tempinho pro JQuery conseguir remover tudo
            this.reabrirSelecaoDeContexto();
        }, 10);
    }

    /**
     * Desfaz o que o layout deixa preso fora do DOM. Esvaziar `.raster-app`
     * remove os elementos, mas não os roots do React, o socket do Hermes nem o
     * listener de resize — e trocar de empresa N vezes acumulava N sockets
     * recebendo notificação e N listeners chamando option() em widgets já
     * destruídos.
     */
    destruirLayout() {
        window.removeEventListener("resize", this.aplicarAltura);

        this.menuLateralRender?.destruir();
        this.topbarRender?.destruir();
        this.notificador?.destruir();

        this.menuLateralRender = null;
        this.topbarRender = null;
        this.notificador = null;
        this.elementoMenuLateral = null;
    }

    sairComAviso(logout) {
        new dxLoadPanel($(".loadpanel"), {
            shadingColor: "rgba(0,0,0,0.4)",
            position: { my: "center", at: "center" },
            showIndicator: true,
            showPane: true,
            shading: true,
            closeOnOutsideClick: false,
            message: "Desconectado...",
            visible: true,
        });

        logout();
    }

    autoHeight() {
        /*
         * 48px é a barra do Design System. No mobile continua valendo o toolbar
         * antigo, que hoje é UMA barra de 46px — eram duas até o commit
         * 10c7d90bc ("ajuste logo mobile", 24/09/2025), que removeu a do logo e
         * esqueceu de baixar este número de 92. Desde então o mobile reservava
         * 46px a mais do que usava, e sobrava um vão no rodapé.
         */
        let alturaCabecalho = ALTURA_TOPBAR;
        if (this.ehMobile()) {
            alturaCabecalho = ALTURA_TOOLBAR_MOBILE;
        }

        // Guardado como campo para o destruirLayout conseguir remover o
        // listener depois — uma função anônima seria impossível de desinscrever.
        this.aplicarAltura = () => {
            const altura = window.innerHeight - alturaCabecalho;
            this.renderControleTabPadrao.instance.option("height", altura);
            if (this.elementoMenuLateral) {
                this.elementoMenuLateral.style.height = `${altura}px`;
            }
        };

        this.aplicarAltura();

        window.addEventListener("resize", this.aplicarAltura);
    }

    logout = () => {
        sairDoEponto();
    };

    /* ---- ganchos das subclasses ---- */

    /**
     * Menus que o usuário pode ver. Endpoints diferentes por tipo de login.
     * @return {Promise<object[]>}
     */
    carregarMenusPermitidos() {
        throw new Error("carregarMenusPermitidos não implementado");
    }

    /**
     * Se as telas fixas de usuário interno entram no grupo de Cadastros.
     * @return {boolean}
     */
    incluiTelasInternas() {
        return false;
    }

    /**
     * Se o login abre a aba fixa de Início. A tela consome os endpoints de
     * indicadores do hera, que o login de embarcadora não serve.
     * @return {boolean}
     */
    abreTelaInicio() {
        return false;
    }

    /** Reabre a tela de seleção correspondente ao tipo de login. */
    reabrirSelecaoDeContexto() {
        throw new Error("reabrirSelecaoDeContexto não implementado");
    }
}
